import React, { useEffect, useState } from 'react';
import { FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { ControlledTypeAhead } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PreferencesTab/CustomComponents/ControlledTypeAhead';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import UnitCollapsible from '@AMIEWEB/Unit/Common/UnitCollapsible';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrderRequirementTypes } from '@AMIEWEB/Order/Store/OrderCreation.selectors';
import { selectOrderSubCertification, selectSubCert } from '@AMIEWEB/Order/Store/Order.selectors';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { Certifications } from 'app/models/Orders/OrderDetails';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { makeStyles } from 'tss-react/mui';
import { AndOrOperator, AndOrText, CollectionType } from 'app/enums/Common';
import { CustomTooltip } from '@AMIEWEB/Common';
import { theme } from 'styles/global-styles';
import { selectTransformedIssuedBy } from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/UnitTab/Store/FacilityUnit.selector';
import { RequirementType } from 'app/enums/Facility';
import { addCertificateHelper } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitDetailDataTransformer';
import { validateDuplicateCertification } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitDetailsFormValidator';
import { SubCertificateUnitTypeAheadDropdown } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitRequirements/Experience/SubCertificate/SubCertificateUnitTypeAhead';
import { handleDeleteSubCert } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitRequirements/Experience/SubCertificate/Helper';
import { FormPageType } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitRequirements/Experience/SubCertificate/SubCertificate.types';
import { isEmpty } from 'lodash';

const radioStyles = {
  '&.Mui-checked': {
    color: `${theme.palette.system.main} !important`,
  },
  '&:hover': {
    color: `${theme.palette.system.main} !important`,
    backgroundColor: 'transparent !important',
  },
};

const useStyles = makeStyles()(theme => ({
  subCertificateError: {
    button: {
      borderBottom: `2px solid ${theme.palette.system.errorRed} !important`,
    },
    span: {
      fontSize: '0.75rem',
      marginLeft: '12px',
      fontWeight: '400',
      marginTop: '3px',
    },
  },
  subCertificate: {
    button: {
      height: '53px',
      border: '0px',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px',
      borderBottom: '1px solid rgba(0, 0, 0, 0.42)!important',
      backgroundColor: `${theme.palette.system.smokeWhite} !important`,
      font: 'normal normal medium 14px / 26px Roboto',
      color: `${theme.palette.system.buttonDarkGray} !important`,
      fontSize: '14px',
      svg: {
        fontSize: '25px !important',
      },
      '&:hover': {
        border: '0px',
        backgroundColor: `${theme.palette.system.hoverGray} !important`,
        borderBottomLeftRadius: '0px',
        borderBottomRightRadius: '0px',
        borderBottom: `1px solid ${theme.palette.system.main} !important`,
      },
      '&:disabled': {
        opacity: '0.6',
        pointerEvents: 'none',
        cursor: 'not-allowed',
        border: '0px',
      },
    },
  },
  checkbox: {
    '& label': {
      '& .MuiButtonBase-root.MuiCheckbox-root.Mui-checked': {
        color: `${theme.palette.system.main} !important`,
      },
      '&:hover .MuiButtonBase-root.MuiCheckbox-root': {
        color: `${theme.palette.system.main} !important`,
      },
    },
  },
  disabledField: {
    opacity: '0.6',
    pointerEvents: 'none',
    cursor: 'not-allowed !important',
  },
  certificateWrapper: {
    display: 'grid',
    gap: '12px',
  },
  actionButtons: {
    color: `${theme.palette.system.neutralGray}`,
    borderRadius: '20px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${theme.palette.system.skyBlue}`,
      color: `${theme.palette.system.main}`,
    },
  },
  selectField: {
    minWidth: 500,
    zIndex: 1000,
  },
}));

export const CertificationsTile = ({
  skillSetIndex,
  groupIndex,
  skillSetField,
  duplicateCetificateData,
  canAddCertificate,
  setCanAddCertificate,
  numberOfSkillSets,
  setDuplicateCetificateData,
}) => {
  const { control, watch } = useFormContext();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { certifications } = useSelector(selectOrderRequirementTypes);
  const subCertificationList = useSelector(selectOrderSubCertification);
  const [certificationOptions, setCertificationOptions] = useState<any[]>([]);
  const [cerificationRequirements, setCerificationRequirements] = useState<any[]>([]);
  const [formattedSubCertificationList, setFormattedSubCertificationList] = useState<any[]>([]);
  const issuedByOptions = useSelector(selectTransformedIssuedBy);
  const [dataList, setDataList] = useState([]);
  const [isCertSelected, setIsCertSelected] = useState(false);
  const [subCertCount, setSubCertCount] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);

  const certificationFieldName = `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements`;

  const {
    fields: requirementFields,
    append: appendRequirement,
    remove: removeRequirement,
  } = useFieldArray({
    control,
    name: certificationFieldName,
  });

  const latestCertificateValue = watch(
    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${
      CollectionType.CertificationsIndex
    }].requirements[${requirementFields?.length - 1}].certificates`,
  );

  const fieldsLength = watch(
    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements`,
  )?.filter(e => e.certificates)?.length;
  const allowAdd =
    requirementFields?.length === 0
      ? true
      : requirementFields?.length > 0 && latestCertificateValue !== undefined && latestCertificateValue !== null;

  useEffect(() => {
    if (canAddCertificate && skillSetIndex === numberOfSkillSets - 1) {
      if (duplicateCetificateData) {
        addCertificateHelper(duplicateCetificateData, appendRequirement);
      } else {
        addCertificate();
      }
    }
    setCanAddCertificate(false);
    setDuplicateCetificateData(null);
  }, [canAddCertificate]);

  const getSelectionInfo = (searchQuery, options) => {
    if (!Array.isArray(options)) return [];
    const searchResult =
      searchQuery && searchQuery?.length >= 2
        ? options?.filter(item =>
            `${item?.abbreviation} - ${item?.credentialName}`
              .toLowerCase()
              .replace(/\s|\.|\(|\)/g, '')
              .includes(searchQuery.toLowerCase().replace(/\s|\.|\(|\)/g, '')),
          )
        : options;
    if (searchQuery?.toUpperCase() === 'CERT') {
      const indexToMove = searchResult?.findIndex(obj => obj?.requirementTypeID === Certifications.CERT_CERTIFICATE);

      if (indexToMove !== -1) {
        const objectToMove = searchResult?.splice(indexToMove, 1)[0];

        searchResult?.unshift(objectToMove);
      }
    }
    return searchResult;
  };
  const dispatch = useDispatch();

  const parseCertifications = () => {
    const certs = [];
    const subCerts = [];
    certifications?.map(rt => {
      rt.category ? certs.push(rt) : subCerts.push(rt);
    });
    setCertificationOptions(certs);
    dispatch(orderDataActions.setsubCertificationList(subCerts));
    if (Array.isArray(certs) && certs?.length > 0) {
      setCerificationRequirements(getSelectionInfo('', certs));
    }
  };

  useEffect(() => {
    if (certifications?.length > 0) {
      parseCertifications();
    }
  }, [certifications]);

  useEffect(() => {
    const formattedCertificationList = [];
    if (cerificationRequirements?.length > 0) {
      cerificationRequirements?.forEach(e => {
        const eachObject = {
          label: e.credentialName ? `${e?.abbreviation} - ${e?.credentialName}` : `${e?.abbreviation}`,
          object: {
            ...e,
            id: e?.requirementTypeID ?? e?.requirementTypeId,
            certificationName: e?.credentialName ? `${e?.abbreviation} - ${e?.credentialName}` : `${e?.abbreviation}`,
          },
        };
        formattedCertificationList.push(eachObject);
      });
    }

    setDataList(formattedCertificationList);
  }, [cerificationRequirements]);

  useEffect(() => {
    const formattedList = subCertificationList?.map(s => {
      return {
        name: s?.abbreviation,
        id: s?.requirementTypeID,
        value: s?.abbreviation,
      };
    });
    setFormattedSubCertificationList(formattedList);
  }, [subCertificationList]);

  const addCertificate = () => {
    const defaultCertificateValue = {
      documentAttachmentRequired: false,
      isRequired: false,
      certificates: null,
      subCertificate: null,
      issuedBy: null,
    };
    if (allowAdd) appendRequirement(defaultCertificateValue);
  };

  const removeCertificate = index => {
    removeRequirement(index);
  };
  React.useEffect(() => {}, [isOpen]);

  return (
    <Grid item xs={12}>
      <UnitCollapsible
        title={t('facility.units.unitDetails.unitRequirements.certifications', {
          certificationCount: isOpen ? requirementFields?.length : fieldsLength ? fieldsLength : 0,
        })}
        iconToolTipContent={t('facility.units.unitDetails.unitRequirements.addCertificate')}
        onClickHandler={addCertificate}
        fieldCount={requirementFields?.length ? requirementFields.length : 0}
        updateIsExpanded={(open: boolean) => setIsOpen(open)}
        children={
          <Grid container>
            {(requirementFields?.length > 1 ||
              (requirementFields?.length === 1 && isCertSelected && subCertCount > 1)) && (
              <Grid item xs={0.8} alignSelf={'center'}>
                <Controller
                  name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].operation`}
                  control={control}
                  defaultValue={
                    skillSetField.collections[CollectionType.CertificationsIndex].operation
                      ? skillSetField.collections[CollectionType.CertificationsIndex].operation
                      : AndOrOperator.And
                  }
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value}
                      onChange={field.onChange}
                      sx={{ alignContent: 'center', minWidth: '75px' }}
                    >
                      <FormControlLabel
                        value={AndOrOperator.And}
                        control={<Radio sx={radioStyles} disableRipple disableFocusRipple />}
                        label={AndOrText.And}
                      />
                      <FormControlLabel
                        value={AndOrOperator.Or}
                        control={<Radio sx={radioStyles} disableRipple disableFocusRipple />}
                        label={AndOrText.Or}
                      />
                    </RadioGroup>
                  )}
                />
              </Grid>
            )}

            <Grid
              item
              xs={
                requirementFields?.length > 1 || (requirementFields?.length === 1 && isCertSelected && subCertCount > 1)
                  ? 11.2
                  : 12
              }
              className={classes.certificateWrapper}
            >
              {requirementFields?.map((requirementField, index) => {
                return (
                  <Grid key={requirementField.id}>
                    <CertificationsInfo
                      dataList={dataList}
                      formattedSubCertificationList={formattedSubCertificationList}
                      appendRequirement={appendRequirement}
                      requirementIndex={index}
                      skillSetIndex={skillSetIndex}
                      removeCertificate={removeCertificate}
                      groupIndex={groupIndex}
                      requirementField={requirementField}
                      issuedByOptions={issuedByOptions}
                      isCertSelected={isCertSelected}
                      setIsCertSelected={setIsCertSelected}
                      setSubCertCount={setSubCertCount}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        }
      />
    </Grid>
  );
};
const CertificationsInfo = ({
  dataList,
  formattedSubCertificationList,
  requirementIndex,
  skillSetIndex,
  removeCertificate,
  groupIndex,
  requirementField,
  issuedByOptions,
  isCertSelected,
  setIsCertSelected,
  setSubCertCount,
  appendRequirement,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    control,
    register,
    setValue,
    formState: { errors },
    trigger,
    watch,
    clearErrors,
    formState: { isSubmitted },
  } = useFormContext();
  const [filteredIssuedBy, setFilteredIssuedBy] = React.useState([]);
  const certificationFieldName = `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements`;
  const requirementValue = watch(certificationFieldName);
  const requirementIndexData = requirementValue?.[requirementIndex];
  const selectedSubCerts = useSelector(selectSubCert);

  const requirementError =
    errors?.skillSetGroup?.groups?.[groupIndex]?.skillsets?.[skillSetIndex]?.collections?.[
      CollectionType.CertificationsIndex
    ]?.requirements?.[requirementIndex];

  const collectionsErrors = errors?.skillSetGroup?.groups?.[groupIndex]?.skillsets?.[skillSetIndex]?.collections || {};
  const requirementsError = collectionsErrors[CollectionType.CertificationsIndex]?.requirements || [];
  const subCertificateError =
    (requirementValue?.length &&
      requirementValue[requirementIndex]?.certificates?.object?.requirementTypeID === RequirementType.cert &&
      requirementsError[requirementIndex]?.subCertificate) ||
    false;

  const removeSubCertFromGlobal = () => {
    const currentRow = watch(
      `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}]`,
    );
    const allSelectedSubCerts = handleDeleteSubCert(
      selectedSubCerts,
      currentRow?.subCertificate?.[0]?.subCertificate?.id,
      skillSetIndex,
    );
    dispatch(orderDataActions.setSelectedSubCert(allSelectedSubCerts));
  };

  const filterIssuedBy = (id: number, disableFields) => {
    if (disableFields) {
      setValue(
        `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].issuedBy`,
        null,
      );
    }
    const filteredIssuedByValues = issuedByOptions.filter(each => each.object.certificationRequirementTypeId === id);
    return filteredIssuedByValues || [];
  };
  const isCertificateCERT =
    requirementValue?.[requirementIndex]?.certificates?.object?.requirementTypeID === RequirementType.cert;

  useEffect(() => {
    if (requirementField.certificates) {
      setFilteredIssuedBy(filterIssuedBy(requirementField.certificates.object.requirementTypeID, false));
      if (requirementField.certificates.object.requirementTypeID === RequirementType.cert) {
        setIsCertSelected(true);
        setSubCertCount(requirementValue?.[requirementIndex]?.subCertificate?.length);
      }
    }
  }, [requirementField]);

  useEffect(() => {
    if (requirementIndexData?.certificates) {
      if (requirementIndexData?.certificates?.object?.requirementTypeID === RequirementType.cert) {
        if (requirementIndexData?.subCertificate?.length > 0) {
          if (!requirementIndexData?.isRequired) {
            setValue(
              `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].isRequired`,
              true,
            );
          }
        } else {
          setValue(
            `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].isRequired`,
            false,
          );
        }
      } else {
        if (!requirementIndexData?.isRequired) {
          setValue(
            `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].isRequired`,
            true,
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirementIndexData?.certificates, requirementIndexData?.subCertificate]);

  return (
    <Grid container xs={12}>
      <Grid container gap={2} xs={11.65}>
        <Grid item xs={2.5}>
          <ControlledTypeAhead
            register={register}
            name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].certificates`}
            label={t('facility.units.unitDetails.unitRequirements.certificate')}
            control={control}
            options={dataList}
            onChange={value => {
              if (value?.object?.requirementTypeID !== RequirementType.cert) {
                removeSubCertFromGlobal();
                setValue(
                  `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].subCertificate`,
                  [],
                );
                setIsCertSelected(false);
              } else {
                setIsCertSelected(true);
              }
              if (value?.object?.requirementTypeID !== filteredIssuedBy[0]?.certificationRequirementTypeId) {
                setFilteredIssuedBy(filterIssuedBy(value?.object?.requirementTypeID, true));
              }
              if (isSubmitted) {
                trigger();
              }
              clearErrors(
                `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].certificates`,
              );
            }}
            customScroll
            disabled={false}
            id={'unitExperienceCertificate'}
            showDropdownIcon={true}
            defaultValue={requirementField?.certificates}
            isHelperText={true}
            isNestedObject={true}
            isError={Boolean(requirementError?.certificates)}
            helperText={
              requirementError?.certificates
                ? `${t('facility.units.unitDetails.unitRequirements.duplicated')} ${t(
                    'facility.units.unitDetails.unitRequirements.certificate',
                  )}`
                : ''
            }
            validateTypeAhead={() => validateDuplicateCertification(requirementIndex, watch(certificationFieldName), t)}
          />
        </Grid>
        <Grid
          item
          xs={2.5}
          className={`${classes.subCertificate} ${subCertificateError ? classes.subCertificateError : ''}`}
          sx={{ cursor: !isCertificateCERT ? 'default' : 'not-allowed' }}
        >
          <CustomTooltip
            standardMargin
            tooltipContent={
              isCertificateCERT ? '' : t('facility.units.unitDetails.unitRequirements.disableSubSertificateMessage')
            }
            hideToolTip={isCertificateCERT}
          >
            <Controller
              name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].subCertificate`}
              control={control}
              defaultValue={requirementField?.subCertificate}
              render={({ field }) => (
                <SubCertificateUnitTypeAheadDropdown
                  name={'subCertificateTypeAheadDropdown'}
                  filterName={
                    !isCertificateCERT
                      ? t('facility.units.unitDetails.unitRequirements.subCertificate')
                      : t('facility.units.unitDetails.unitRequirements.subCertificateRequired')
                  }
                  options={formattedSubCertificationList}
                  isMultiSelect
                  value={field.value}
                  minWidth={'200px'}
                  apply={newValue => {
                    field.onChange(newValue);
                    if (isCertSelected) {
                      setSubCertCount(newValue?.length);
                    }
                    clearErrors(
                      `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].subCertificate`,
                    );
                    if (isSubmitted) {
                      trigger();
                    }
                  }}
                  fallbackPlacements={['top', 'right', 'left']}
                  removeCertificate={removeCertificate}
                  requirementIndex={requirementIndex}
                  skillSetIndex={skillSetIndex}
                  groupIndex={groupIndex}
                  appendRequirement={appendRequirement}
                  isDisabled={!isCertificateCERT}
                  selectedSubCerts={selectedSubCerts}
                  parentPage={FormPageType.Order}
                  controlErrorText={subCertificateError ? t('required') : ''}
                  customClass={classes.selectField}
                  hasControlError={subCertificateError}
                />
              )}
              rules={{
                validate: (value) => {
                  const duplicateError = validateDuplicateCertification(requirementIndex, watch(certificationFieldName), t);
                  if (duplicateError) {
                    return duplicateError;
                  }
                  const currentRequirement = watch(
                    `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}]`,
                  );
                  if (
                    currentRequirement?.certificates?.object?.requirementTypeID === RequirementType.cert &&
                    (!value || (Array.isArray(value) && value.length === 0))
                  ) {
                    return t('required');
                  }
                  return true;
                },
              }}
            />
          </CustomTooltip>
        </Grid>
        <Grid
          item
          xs={2.5}
          marginRight={2}
          className={`${
            Boolean(requirementValue?.[requirementIndex]?.certificates) && filteredIssuedBy.length && !isCertificateCERT
              ? ''
              : classes.disabledField
          }`}
        >
          <ControlledTypeAhead
            register={register}
            name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].issuedBy`}
            label={t('facility.units.unitDetails.unitRequirements.issuedBy')}
            control={control}
            options={filteredIssuedBy}
            onChange={() => {
              if (isSubmitted) {
                trigger();
              }
              clearErrors(
                `skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].issuedBy`,
              );
            }}
            customScroll
            disabled={false}
            id={'unitExperienceIssuedBy'}
            showDropdownIcon={true}
            defaultValue={requirementField?.issuedBy}
            isHelperText={true}
            isNestedObject={true}
            isError={
              Boolean(requirementValue?.[requirementIndex]?.issuedBy) ? Boolean(requirementError?.issuedBy) : false
            }
            helperText={
              Boolean(requirementValue?.[requirementIndex]?.issuedBy)
                ? requirementError?.issuedBy
                  ? `${t('facility.units.unitDetails.unitRequirements.duplicated')} ${t(
                      'facility.units.unitDetails.unitRequirements.issuedBy',
                    )}`
                  : ''
                : ''
            }
            validateTypeAhead={() => validateDuplicateCertification(requirementIndex, watch(certificationFieldName), t)}
          />
        </Grid>
        <Grid item sx={{ alignSelf: 'center' }} className={classes.disabledField} marginRight={2}>
          <Controller
            name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].isRequired`}
            control={control}
            defaultValue={requirementField?.isRequired}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value}
                isdisabled={field.value}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('facility.units.unitDetails.unitRequirements.required')}
                checkboxName={'experienceCertificationsRequired'}
                id={'experienceCertificationsRequired'}
              />
            )}
          />
        </Grid>
        <Grid
          item
          sx={{ alignSelf: 'center' }}
          className={`${classes.checkbox} ${
            Boolean(requirementValue?.[requirementIndex]?.certificates) ? '' : classes.disabledField
          }`}
        >
          <Controller
            name={`skillSetGroup.groups[${groupIndex}].skillsets[${skillSetIndex}].collections[${CollectionType.CertificationsIndex}].requirements[${requirementIndex}].documentAttachmentRequired`}
            control={control}
            defaultValue={requirementField?.documentAttachmentRequired}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('facility.units.unitDetails.unitRequirements.attachDocument')}
                checkboxName={'experienceCertificationsAttachDocument'}
                id={'experienceCertificationsAttachDocument'}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item gap={2} sx={{ alignSelf: 'center', textAlignLast: 'center' }} xs={0.35}>
        <CustomTooltip
          standardMargin
          tooltipContent={t('facility.units.unitDetails.unitRequirements.deleteCertificate')}
        >
          <DeleteOutlinedIcon
            className={classes.actionButtons}
            onClick={async () => {
              await removeSubCertFromGlobal();
              await removeCertificate(requirementIndex);
              if (isCertificateCERT) {
                setIsCertSelected(false);
                setSubCertCount(0);
              }
              if (isSubmitted) {
                trigger();
              }
            }}
          />
        </CustomTooltip>
      </Grid>
    </Grid>
  );
};
